import * as React from 'react';
import {ExerciseLibraryModalService} from './ExerciseLibraryModal.service';
import {
  Exercise,
  ExerciseLibraryModalState,
  SelectedExerciseData,
  ExerciseFilterOptions,
  ModalTab,
  CreateExerciseFormData,
  ExerciseCreationState,
} from './ExerciseLibraryModal.type';

const INITIAL_STATE: ExerciseLibraryModalState = {
  exercises: [],
  selectedExercise: null,
  searchTerm: '',
  currentPage: 1,
  totalPages: 1,
  isLoading: false,
  hasMore: true,
  exerciseData: {
    exercise: null,
    series: '',
    reps: '',
    weight: '',
    time: '',
    restTime: '',
    notes: '',
  },
};

const INITIAL_CREATION_STATE: ExerciseCreationState = {
  activeTab: 'library',
  isCreatingExercise: false,
  formData: {
    name: '',
    description: '',
    recordEnabled: false,
    exerciseType: undefined,
    videoUrl: '',
  },
  videoUploadUrl: '',
  isVideoUploading: false,
  createdExercise: undefined,
};

export const useExerciseLibraryModal = (initialData?: SelectedExerciseData) => {
  const [state, setState] =
    React.useState<ExerciseLibraryModalState>(INITIAL_STATE);
  const [creationState, setCreationState] =
    React.useState<ExerciseCreationState>(INITIAL_CREATION_STATE);
  const searchTimeoutRef = React.useRef<NodeJS.Timeout>();

  const updateState = React.useCallback(
    (updates: Partial<ExerciseLibraryModalState>) => {
      setState((prevState) => ({...prevState, ...updates}));
    },
    [],
  );

  const updateCreationState = React.useCallback(
    (updates: Partial<ExerciseCreationState>) => {
      setCreationState((prevState) => ({...prevState, ...updates}));
    },
    [],
  );

  const resetState = React.useCallback(() => {
    const resetData = initialData
      ? {
          ...INITIAL_STATE,
          selectedExercise: initialData.exercise,
          exerciseData: initialData,
        }
      : INITIAL_STATE;
    setState(resetData);
    setCreationState(INITIAL_CREATION_STATE);
  }, [initialData]);

  React.useEffect(() => {
    if (initialData) {
      const isValid = !!initialData.exercise?.id && !!initialData.exercise?.name;
      if (!isValid) {
        console.warn('[ExerciseLibraryModal] Invalid initialData:', initialData);
      }
      setState({
        ...INITIAL_STATE,
        selectedExercise: initialData.exercise,
        exerciseData: initialData,
      });
    }
  }, [initialData]);

  const setSelectedExercise = React.useCallback((exercise: Exercise | null) => {
    setState((prevState) => ({
      ...prevState,
      selectedExercise: exercise,
      exerciseData: {
        ...prevState.exerciseData,
        exercise,
      },
    }));
  }, []);

  const updateExerciseData = React.useCallback(
    (field: keyof SelectedExerciseData, value: string) => {
      updateState({
        exerciseData: {
          ...state.exerciseData,
          [field]: value,
        },
      });
    },
    [state.exerciseData, updateState],
  );

  const searchExercises = React.useCallback(
    async (
      searchTerm: string = '',
      page: number = 1,
      shouldReplace: boolean = true,
    ) => {
      updateState({isLoading: true});

      try {
        const result = await ExerciseLibraryModalService.searchExercises(
          page,
          searchTerm,
        );

        updateState({
          exercises: shouldReplace
            ? result.list
            : [...state.exercises, ...result.list],
          currentPage: result.currentPage,
          totalPages: result.totalPages,
          hasMore: result.currentPage < result.totalPages,
          isLoading: false,
        });
      } catch (error) {
        console.error('Error searching exercises:', error);
        updateState({isLoading: false});
      }
    },
    [state.exercises, updateState],
  );

  const handleSearchChange = React.useCallback(
    (searchTerm: string) => {
      updateState({searchTerm});

      // Debounce search
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      searchTimeoutRef.current = setTimeout(() => {
        searchExercises(searchTerm, 1, true);
      }, 300);
    },
    [searchExercises, updateState],
  );

  const loadMore = React.useCallback(async () => {
    if (state.isLoading || !state.hasMore) return;

    await searchExercises(state.searchTerm, state.currentPage + 1, false);
  }, [
    state.isLoading,
    state.hasMore,
    state.searchTerm,
    state.currentPage,
    searchExercises,
  ]);

  const canSelectExercise = React.useCallback((): boolean => {
    return ExerciseLibraryModalService.validateExerciseData(state.exerciseData);
  }, [state.exerciseData]);

  const getSelectedExerciseData =
    React.useCallback((): SelectedExerciseData => {
      return state.exerciseData;
    }, [state.exerciseData]);

  // Tab management functions
  const setActiveTab = React.useCallback(
    (tab: ModalTab) => {
      updateCreationState({activeTab: tab});
    },
    [updateCreationState],
  );

  // Exercise creation functions
  const updateFormData = React.useCallback(
    (updates: Partial<CreateExerciseFormData>) => {
      updateCreationState({
        formData: {...creationState.formData, ...updates},
      });
    },
    [creationState.formData, updateCreationState],
  );

  const setVideoUploadUrl = React.useCallback(
    (url: string) => {
      updateCreationState({videoUploadUrl: url});
      updateFormData({videoUrl: url});
    },
    [updateCreationState, updateFormData],
  );

  const setVideoUploading = React.useCallback(
    (isUploading: boolean) => {
      updateCreationState({isVideoUploading: isUploading});
    },
    [updateCreationState],
  );

  const createExercise = React.useCallback(async (): Promise<boolean> => {
    if (!creationState.formData.name || !creationState.formData.description) {
      return false;
    }

    updateCreationState({isCreatingExercise: true});

    try {
      // Import MutationExercise dynamically to avoid circular dependencies
      const {MutationExercise} = await import(
        '../../../../services/exercise/MutationExercise'
      );

      const result = await MutationExercise(
        creationState.formData,
        () => {}, // setSubmitting callback (not needed here)
        () => {}, // setProgress callback (not needed here)
      );

      if (result.status === 'success' && result.exercise) {
        const newExercise: Exercise = {
          id: result.exercise.id,
          name: result.exercise.name,
          description: result.exercise.description,
          image: result.exercise.image,
          animatedImage: result.exercise.animatedImage,
        };

        updateCreationState({
          createdExercise: newExercise,
          isCreatingExercise: false,
        });

        // Automatically select the created exercise
        setSelectedExercise(newExercise);

        // Switch back to library tab
        setActiveTab('library');

        return true;
      }

      updateCreationState({isCreatingExercise: false});
      return false;
    } catch (error) {
      console.error('Error creating exercise:', error);
      updateCreationState({isCreatingExercise: false});
      return false;
    }
  }, [
    creationState.formData,
    updateCreationState,
    setSelectedExercise,
    setActiveTab,
  ]);

  // Initialize with empty search to load initial exercises
  React.useEffect(() => {
    searchExercises('', 1, true);
  }, []);

  // Cleanup timeout on unmount
  React.useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return {
    // State
    exercises: state.exercises,
    selectedExercise: state.selectedExercise,
    searchTerm: state.searchTerm,
    isLoading: state.isLoading,
    hasMore: state.hasMore,
    exerciseData: state.exerciseData,

    // Creation State
    activeTab: creationState.activeTab,
    isCreatingExercise: creationState.isCreatingExercise,
    formData: creationState.formData,
    videoUploadUrl: creationState.videoUploadUrl,
    isVideoUploading: creationState.isVideoUploading,
    createdExercise: creationState.createdExercise,

    // Actions
    setSelectedExercise,
    updateExerciseData,
    handleSearchChange,
    loadMore,
    resetState,
    canSelectExercise,
    getSelectedExerciseData,

    // Tab and Creation Actions
    setActiveTab,
    updateFormData,
    setVideoUploadUrl,
    setVideoUploading,
    createExercise,
  };
};
